import type { HttpClient } from '../http/HttpClient.ts';

interface Named {
  id: string;
  name: string;
}
interface ItemBrief {
  id: string;
  number: number;
  key: string;
  title: string;
}
/** Where a question or request lives (lists that span projects). */
export interface Place {
  org: { slug: string; name: string };
  project: { key: string; name: string };
}

export interface AgentQuestion {
  id: string;
  agent: Named;
  owner: Named;
  asked: Named;
  area: string | null;
  text: string;
  choices: string[];
  /** Only one of the choices is accepted. */
  choicesOnly: boolean;
  default: string | null;
  deadline: string | null;
  status: 'open' | 'answered' | 'expired';
  answer: string | null;
  answeredBy: Named | null;
  answeredAt: string | null;
  item: ItemBrief | null;
  sessionId: string | null;
  createdAt: string;
}

export interface DeletionRequest {
  id: string;
  agent: Named;
  owner: Named;
  item: ItemBrief | null;
  itemLabel: string;
  reason: string;
  status: 'open' | 'approved' | 'declined';
  decidedBy: Named | null;
  decidedAt: string | null;
  note: string | null;
  createdAt: string;
}

/** An agent working in a project (to @mention, or hand work to when it's yours). */
export interface ProjectAgent {
  id: string;
  name: string;
  owner: Named;
  mine: boolean;
  /** Others' @mentions reach it (its owner's always do). */
  acceptsMentions: boolean;
  /** Only for your own agents: waiting on its inbox now, and when it last did. */
  listening?: boolean;
  lastListenedAt?: string | null;
}

/** What agents are asking about an item (on the item's detail). */
export interface ItemAgentRequests {
  waitingOn: { questionId: string; asked: Named; agent: Named; owner: Named }[];
  deletion: { id: string; reason: string; agent: Named; owner: Named }[];
}

export type Answer = { choice: string } | { text: string };

const base = (org: string, key: string) =>
  `/orgs/${encodeURIComponent(org)}/projects/${encodeURIComponent(key)}`;

/** Messages between people and agents: answering, deciding, handing work over. */
export class AgentMessagingApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  /** Open questions asked of you, and deletion requests from your agents. */
  pending(): Promise<{
    questions: (AgentQuestion & Place)[];
    deletions: (DeletionRequest & Place)[];
  }> {
    return this.#http.get('/me/agent-requests');
  }

  questions(
    org: string,
    key: string,
    query: { status?: 'open' | 'answered' | 'expired'; mine?: boolean; item?: string } = {},
  ): Promise<{ questions: AgentQuestion[] }> {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    if (query.mine) params.set('mine', 'true');
    if (query.item) params.set('item', query.item);
    const qs = params.toString();
    return this.#http.get(`${base(org, key)}/agent-questions${qs ? `?${qs}` : ''}`);
  }

  answer(
    org: string,
    key: string,
    id: string,
    answer: Answer,
  ): Promise<{ question: AgentQuestion }> {
    return this.#http.post(
      `${base(org, key)}/agent-questions/${encodeURIComponent(id)}/answer`,
      answer,
    );
  }

  deletions(
    org: string,
    key: string,
    query: { status?: 'open' | 'approved' | 'declined' } = {},
  ): Promise<{ requests: DeletionRequest[] }> {
    const qs = query.status ? `?status=${query.status}` : '';
    return this.#http.get(`${base(org, key)}/deletion-requests${qs}`);
  }

  decide(
    org: string,
    key: string,
    id: string,
    approve: boolean,
    note?: string,
  ): Promise<{ request: DeletionRequest }> {
    return this.#http.post(
      `${base(org, key)}/deletion-requests/${encodeURIComponent(id)}/${approve ? 'approve' : 'decline'}`,
      note ? { note } : {},
    );
  }

  agents(org: string, key: string): Promise<{ agents: ProjectAgent[] }> {
    return this.#http.get(`${base(org, key)}/agents`);
  }

  handTo(
    org: string,
    key: string,
    input: { agentId: string; text: string; item?: number },
  ): Promise<unknown> {
    return this.#http.post(`${base(org, key)}/agent-messages`, input);
  }
}
